// Search/ranking helpers for the general products list.
// Splits results into "exact" matches (by name or SKU) and "suggested"
// (close/fuzzy) matches so the UI can show them as separate groups.

const ACCENT_MARKS_RE = new RegExp("[\\u0300-\\u036f]", "g");

const normalize = (str) =>
  (str || "")
    .toString()
    .toLowerCase()
    .normalize("NFD")
    .replace(ACCENT_MARKS_RE, "") // strip accents
    .replace(/[\s-]/g, "") // strip spaces/dashes (kept for SKU-style matches)
    .trim();

const normalizeWords = (str) =>
  (str || "")
    .toString()
    .toLowerCase()
    .normalize("NFD")
    .replace(ACCENT_MARKS_RE, "")
    .trim();

const levenshteinDistance = (a, b) => {
  if (a === b) return 0;
  if (a.length === 0) return b.length;
  if (b.length === 0) return a.length;

  let prevRow = Array.from({ length: b.length + 1 }, (_, i) => i);
  for (let i = 1; i <= a.length; i += 1) {
    const currRow = [i];
    for (let j = 1; j <= b.length; j += 1) {
      const cost = a[i - 1] === b[j - 1] ? 0 : 1;
      currRow[j] = Math.min(
        prevRow[j] + 1, // deletion
        currRow[j - 1] + 1, // insertion
        prevRow[j - 1] + cost // substitution
      );
    }
    prevRow = currRow;
  }
  return prevRow[b.length];
};

const similarityRatio = (a, b) => {
  const maxLen = Math.max(a.length, b.length);
  if (maxLen === 0) return 1;
  return 1 - levenshteinDistance(a, b) / maxLen;
};

const getProductSearchFields = (product) => {
  const variants = product.variants_array || [];
  return {
    name: product.name || "",
    matchingString: product.matching_string || "",
    skus: variants.map((v) => v.tivendo_id).filter(Boolean),
    variantNames: variants.map((v) => v.variant).filter(Boolean),
  };
};

// Returns { isExact, score (0-1) } for how well a product matches the query.
export const scoreProduct = (product, query) => {
  const q = normalize(query);
  const qWords = normalizeWords(query)
    .split(/\s+/)
    .filter(Boolean);

  if (!q) return { isExact: false, score: 0 };

  const fields = getProductSearchFields(product);
  const normName = normalize(fields.name);
  const normMatching = normalize(fields.matchingString);
  const normSkus = fields.skus.map((sku) => normalize(sku));
  const normVariantNames = fields.variantNames.map((v) => normalize(v));

  const isExact =
    normName === q ||
    normMatching === q ||
    normSkus.includes(q) ||
    normVariantNames.includes(q);

  if (isExact) return { isExact: true, score: 1 };

  let score = 0;

  // Substring matches (current word/SKU is contained in the field, or vice versa)
  if (normName.includes(q) || q.includes(normName)) score = Math.max(score, 0.85);
  if (normMatching.includes(q)) score = Math.max(score, 0.8);
  if (normSkus.some((sku) => sku.includes(q))) score = Math.max(score, 0.9);
  if (normVariantNames.some((v) => v.includes(q) || q.includes(v)))
    score = Math.max(score, 0.75);

  // Multi-word queries: reward names that contain most of the query's words
  if (qWords.length > 1) {
    const nameWords = normalizeWords(fields.name).split(/\s+/).filter(Boolean);
    const matchedWords = qWords.filter((qw) =>
      nameWords.some((nw) => nw.includes(qw) || qw.includes(nw))
    );
    score = Math.max(score, (matchedWords.length / qWords.length) * 0.7);
  }

  // Typo-tolerant fallback via Levenshtein similarity
  score = Math.max(
    score,
    similarityRatio(q, normName) * 0.6,
    similarityRatio(q, normMatching) * 0.55
  );

  return { isExact: false, score };
};

const SUGGESTED_THRESHOLD = 0.35;

// Splits products into exact matches and close/suggested matches for a query.
// When the query is empty, returns everything as "exact" (i.e. unfiltered).
export const searchProducts = (products, query) => {
  const q = (query || "").trim();
  if (!q) {
    return { exactMatches: products, suggestedMatches: [] };
  }

  const scored = products
    .map((product) => ({ product, ...scoreProduct(product, q) }))
    .filter(({ isExact, score }) => isExact || score >= SUGGESTED_THRESHOLD);

  const exactMatches = scored
    .filter(({ isExact }) => isExact)
    .map(({ product }) => product);

  const suggestedMatches = scored
    .filter(({ isExact }) => !isExact)
    .sort((a, b) => b.score - a.score)
    .map(({ product }) => product);

  return { exactMatches, suggestedMatches };
};
